import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = 'invoice_footer_customisation';

export const UpdateFooterSchema = z.array(
  z
    .union([z.null(), z.string(), z.object({ field: z.string(), content: z.string() })])
    .transform(val => (val == null ? false : val)),
);

export type UpdateFooter = z.infer<typeof UpdateFooterSchema>;

export async function updateInvoiceFooterFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateFooter,
) {
  const instance = await axiosInstance;
  return instance.put(URL, { fields: data });
}

export function useUpdateInvoiceFooter() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateFooter>({
    mutationFn: (data: UpdateFooter) => updateInvoiceFooterFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoiceFooter'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpdateFooter, 'updateInvoiceFooter'>(
    'updateInvoiceFooter',
    mutation,
  );
}
